import { pickBy, get } from 'lodash-es';
import * as React from 'react';
import Sticky from 'react-stickynode';
import { StickyContainer } from 'react-sticky';

const { Row, Col, Breakpoints, OnWhen } = require('@cvent/carina-layout');
const { ButtonGroup } = require('@cvent/carina-button-group');
const { GridIcon, MapPinIcon } = require('@cvent/carina-icon');
const { Alerts } = require('@cvent/carina-alert');
import { Search, Venues, SearchApi } from '@weddingspot/ws-api-client';
import {
    RingsSpinner,
    LoadingSpinner,
    FeatureFlagContext,
    FLAG_SRP_PRICE_VENUE_CTA,
    FLAG_SHOPPING_CART_FOR_SRP,
    Typeahead,
} from '@weddingspot/ws-core-components';

import { VenueCard } from './VenueCard';
import { BudgetBanner } from './BudgetBanner';
import { VenueMapComponent } from './MapView';
import { PaginationComponent } from './pagination';
import { FilterSidebar, resetAllFilter } from './FiltersSidebar';
import { MarketLinksContainer } from './MarketLinksContainer';
import {
    getVariantFromMatch,
    generateAllMetadata,
    LogPerformance,
    generateBackfillBannerText,
    SRPMetadata,
    ApiResponseToFormik,
    URLToSearchApiParams,
    formikValuesToUrl,
} from './utils';
import { log } from '../../utils/logging';
import { useLocation, useHistory } from 'react-router';
import { Formik, useFormikContext } from 'formik';

import * as qs from 'query-string';
import cn from 'classnames';

import { useDebouncedCallback } from 'use-debounce';

import Helmet from 'react-helmet';
import { LinkFarm } from './LinkFarm';
import { SeoBlurb } from './SeoBlurb';
import { CuratedLists } from './CuratedBuckets';
import * as analytics from '@weddingspot/ws-analytics';

const { AnalyticsEvent, logEvent } = analytics;

import { RouteComponentProps } from 'react-router';

import {
    useVenues,
    usePageMetadata,
    usePersonalizedData,
    useDeepCompareMemoize,
    useFilters,
    useLogicalAndDependencies,
    useDirectoryImpressionsEffect,
} from './hooks';
import { ComponentWithSSRHooks } from './hooks/ApiHookCreator';
import Error500 from '../errors';
import NotFound from '../404';
import BlurryLoader from '../BlurryLoader';
import { Redirect } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { LiveAgent } from '@weddingspot/ws-core-components';
import { setCurUserProps } from '@weddingspot/ws-analytics';
import { FilterBar } from './FilterBar';
import isFromMarketListForMapViewAB from '../../utils/helpers/isFromMarketListForMapViewAB';
import { HOGAN_CONFIG } from '../../constants/config';

const _styles = require('./index.scss');

// TODO: Refactor the VenueListComponent to separate file
interface VenueListProps {
    searchResults?: Search.SearchResults;
    personalizedData?: Search.SearchPersonalizedData;
    venuesIsLoading: boolean;
    srpVariant?: 'affordable' | 'rd';
    pageMetadata?: SRPMetadata;
    locationsDirty?: boolean;
    metadata?: object;
    viewType?: 'map' | 'grid';
    isDW?: boolean;
}

const VenueListComponent = React.memo((props: VenueListProps) => {
    LogPerformance('VenueListComponent render');

    const { searchResults, personalizedData, srpVariant, pageMetadata, locationsDirty, metadata, viewType, isDW } = props;

    if (!searchResults) {
        return (
            <div className='srp-venues-spinner'>
                <RingsSpinner animate={true} />
            </div>
        );
    }

    const totalResults = searchResults.venues.length + searchResults.backfill_venues.length;
    const isDestinationWeddings = pageMetadata && pageMetadata.location.market === 'Destination Weddings';
    const isFirstPage = !pageMetadata || pageMetadata.page === 1;
    const formikContext = useFormikContext<SRPFormikValues>();

    const handleSearchResultClick = React.useCallback(
        (venueInfo: Search.SearchResult, position: number) => {
            logEvent(AnalyticsEvent.EVTNM_CLICKED_SEARCH_RESULT, {
                'Venue ID': venueInfo.id,
                Position: position,
                '# Results On Page': totalResults,
                'Award Winner': venueInfo.award_winner,
                'SRP Version': 2,
            });

            SearchApi.logDirectoryClickthroughs({
                venue_id: venueInfo.id,
                position: position,
                metrics_metatype: srpVariant === 'rd' ? Venues.VenueMetricsMetaType.RD_METRIC : Venues.VenueMetricsMetaType.WEDDING_METRIC,
            });
        },
        [totalResults]
    );

    const renderVenueCard = (venueInfo: Search.SearchResult, idx: number) => {
        let venuePersonalized;
        if (personalizedData) {
            venuePersonalized = personalizedData.venues.find((v) => v.venue_id === venueInfo.id);
        }
        const isFavorite = venuePersonalized ? venuePersonalized.is_favorite : false;
        const estimate = venuePersonalized ? venuePersonalized.spot_estimate : undefined;

        const _metadata = {
            page: pageMetadata ? pageMetadata.page : 1,
            metadata: metadata,
        };

        return (
            <VenueCard
                key={venueInfo.id}
                venue={venueInfo}
                spotEstimate={estimate}
                isFavorite={isFavorite}
                handleSearchResultClick={handleSearchResultClick}
                srpVariant={srpVariant}
                deferImageLoad={true}
                metadata={_metadata}
                isDestinationWeddings={isDestinationWeddings}
                personalizedData={personalizedData}
                position={idx}
                viewType={viewType}
            />
        );
    };

    const notFoundMessage =
        searchResults.venues_count === 0 && isFirstPage ? (
            <div className={_styles.noResultMessage}>
                <Alerts
                    alerts={[
                        {
                            appearance: 'info',
                            text: 'No results, consider adjusting your filters ' + '(location, spelling, or other search criteria).',
                            buttonText: 'Reset Filters',
                            onButtonPress: () => {
                                resetAllFilter(formikContext);
                            },
                        },
                    ]}
                />
            </div>
        ) : null;

    const mainResults = searchResults.venues.map(renderVenueCard);

    const showBanner =
        mainResults && (props.srpVariant === 'affordable' || props.srpVariant === undefined) && !isDW && pageMetadata?.page === 1;

    if (showBanner) {
        if (mainResults.length >= 15) {
            mainResults.splice(15, 0, <BudgetBanner venue={searchResults.venues[15]} />);
        } else {
            mainResults.push(<BudgetBanner />);
        }
    }

    const backfillResults = searchResults.backfill_venues.map((venueInfo, idx) => {
        return renderVenueCard(venueInfo, idx + searchResults.venues.length);
    });

    const backfillBanner =
        (searchResults.venues.length || isFirstPage) && searchResults.backfill_venues.length ? (
            <div className='srp-backfill'>{generateBackfillBannerText(pageMetadata, locationsDirty)}</div>
        ) : undefined;

    return (
        <>
            <div
                className={cn('srp-venues-container', {
                    ['srp-venues-loading']: props.venuesIsLoading,
                })}
            >
                {notFoundMessage || mainResults}
                {backfillBanner}
                {backfillResults.length > 0 && backfillResults}
            </div>
            <PaginationComponent itemsCount={searchResults.venues_count + searchResults.backfill_venues_count} />
        </>
    );
});

export type SRPFormikValues = {
    budget?: [number, number | null];
    sr?: number;
    byo?: boolean;
    guestCount?: [number, number | null];
    region: Record<string, boolean>;
    market: Record<string, boolean>;
    styles?: Record<string, boolean>;
    bbox?: [number, number, number, number];
};

export const SearchResultPageComponent = (props: RouteComponentProps<SRPRouteMatchProps>) => {
    const { flagEnabled, isInitialized: isFlagsInitialized } = React.useContext(FeatureFlagContext);

    // React router state
    const history = useHistory();
    LogPerformance('start render SRP Component');
    const reactRouterLocation = useLocation();
    const match = props.match;
    const search = useMemo(() => qs.parse(reactRouterLocation.search), [reactRouterLocation.search]);
    const variant = getVariantFromMatch(match);
    const searchParamsMemoized = useDeepCompareMemoize(URLToSearchApiParams(match, search));

    /*
    When there's a navigation event that doesn't cause this component to get re-mounted,
    we want to re-fetch filters an metadata along with the search results. e.g. clicking
    a <Link> component from SRP -> SRP.

    The value of the refetchSignal state variable doesn't really matter here, it's just used
    to force a one-time state change
    */
    const [refetchSignal, setRefetchSignal] = React.useState({});
    const refetchDeps = useLogicalAndDependencies([searchParamsMemoized, refetchSignal]);

    // We want to refetch data when any of the route match params changes, except map
    // which isn't related to API call
    const [venues, venuesIsLoading, venuesStatus] = useVenues([useDeepCompareMemoize(match), searchParamsMemoized]);
    const [searchFilters, searchFiltersIsLoading, searchFiltersStatus] = useFilters(refetchDeps);
    const [metadata, metadataIsLoading, metadataStatus] = usePageMetadata(refetchDeps);

    useDirectoryImpressionsEffect(venues, match, search);

    // Search filters data handling
    const initialValues = React.useMemo(() => {
        return ApiResponseToFormik(searchFilters);
    }, [searchFilters]);

    // Handle map/grid view toggling

    const [viewMode, setViewMode] = React.useState<'grid' | 'map'>('grid');
    const [gridTouched, setGridTouched] = React.useState(viewMode === 'grid');
    const [mapTouched, setMapTouched] = React.useState(viewMode === 'map');
    const _venues = venues as Search.SearchResults;

    const breadcrumbsRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (search.map === '1' && viewMode !== 'map') {
            setViewMode('map');
            setMapTouched(true);
            setGridTouched(false);
        }
    }, []);

    const getVisitEventData = () => {
        const selectedMarket = metadata?.location?.market;
        const selectedMarketLabel = selectedMarket || 'All venues';
        const selectedRegion = metadata?.location?.region;
        const selectedStyle = metadata?.style_name;
        const allStylesLandingPage = (selectedMarket && !selectedStyle) || false;
        const totalResults = (_venues?.venues?.length || 0) + (_venues?.backfill_venues?.length || 0);

        return {
            Variant: variant === 'affordable' ? 'Affordable' : variant === 'rd' ? 'Rehearsal dinners' : 'Regular',
            'Shopping Cart Shown': isFlagsInitialized && flagEnabled(FLAG_SHOPPING_CART_FOR_SRP),
            Market: selectedMarketLabel,
            Region: selectedRegion,
            Style: selectedStyle,
            'Page Number': parseInt(search.page as string, 10) || 1,
            'All Styles Landing Page': allStylesLandingPage,
            'Listings visible': totalResults,
            'SRP Version': 2,
            'With favorite button': !flagEnabled('favorite_button_hidden_on_SRP'),
        };
    };

    React.useEffect(() => {
        // Don't do anything before initial data loads
        if (!metadata || !_venues) {
            return;
        }

        if (breadcrumbsRef.current) {
            const breadcrumbsScrollContainer = breadcrumbsRef.current.querySelector('.Breadcrumbs--wrapper');
            if (breadcrumbsScrollContainer) {
                breadcrumbsScrollContainer.scrollLeft = 1000;
            }
        }

        const eventData = getVisitEventData();

        const _buckets = metadata?.buckets || [];
        if (_buckets.length > 0) {
            const params = {
                Market: eventData.Market,
                Region: eventData.Region,
                Style: eventData.Style,
                'Shown Buckets': _buckets.map((bucket) => bucket.title),
                'SRP Version': 2,
            };
            logEvent(AnalyticsEvent.EVTNM_SHOWN_VENUE_CURATION_BUCKET, params);
        }
    }, useLogicalAndDependencies([metadata, _venues]));

    React.useEffect(() => {
        if (!metadata || !_venues) {
            return;
        }
        if (isFlagsInitialized) {
            logEvent(AnalyticsEvent.EVTNM_WEDDING_VENUE_DIRECTORY, getVisitEventData());
        }
    }, [...useLogicalAndDependencies([metadata, _venues]), isFlagsInitialized]);

    React.useEffect(() => {
        if (viewMode === 'map') {
            search.map = '1';
            logEvent(AnalyticsEvent.EVTNM_VISITED_MAP_VIEW, getVisitEventData());
        } else {
            delete search.map;
        }
        history.replace({
            pathname: reactRouterLocation.pathname,
            search: qs.stringify(search),
            state: { normalization: true },
        });
    }, [viewMode]);

    // START METADATA PART
    // Whether or not the locations filter was touched by the user
    const locationsTouched = React.useRef(false);

    const { metadataParsed, metaTitle, titleText, metaDescription, searchResultsText, keywords, renderedBreadcrumbs, metaLinks, isDw } =
        generateAllMetadata(metadata, venues, search, match, locationsTouched.current, variant, viewMode === 'map');

    const metadataParsedMemoized = useDeepCompareMemoize(metadataParsed);

    // END METADATA PART

    const [isViewChosen, setViewChosen] = useState(false);

    useEffect(() => {
        if (isFlagsInitialized && flagEnabled('default_map_view') && isFromMarketListForMapViewAB(match.params?.market)) {
            setViewMode('map');
            setMapTouched(true);
            setGridTouched(false);
        }
        if (isFlagsInitialized) {
            setViewChosen(true);
        }
    }, [isFlagsInitialized]);

    // Personalized data state initialization
    const personalizedData = usePersonalizedData(venues);
    const priceVenuesCTAEnabled = flagEnabled(FLAG_SRP_PRICE_VENUE_CTA);
    const shoppingCartFlag = isFlagsInitialized && flagEnabled(FLAG_SHOPPING_CART_FOR_SRP);
    React.useEffect(() => {
        if (!personalizedData) {
            return;
        }
        setCurUserProps({
            'Price venue CTA test on SRP': priceVenuesCTAEnabled ? 'B' : 'A',
        });
    }, [priceVenuesCTAEnabled]);

    React.useEffect(() => {
        if (shoppingCartFlag) {
            setCurUserProps({
                'SRP with cart': 'true',
            });
        }
    }, [shoppingCartFlag]);

    // Update the URL whenever filter values change
    const [onFormChange, cancelOnFormChange] = useDebouncedCallback((values: SRPFormikValues) => {
        log(values.bbox);
        LogPerformance('onFormChange+debaunce', { values });
        const location = metadata?.location;
        const { pathname, search: newSearch } = formikValuesToUrl(values, { variant, search: search, metaLocation: location || undefined });
        history.replace({
            pathname: pathname,
            search: qs.stringify(newSearch),
            state: { normalization: true },
        });
        const selectedRegions = Object.entries(pickBy(values.region)).map(([key]) => key.split(':')[0]);
        const selectedStyles = Object.entries(pickBy(values.styles)).map(([key]) => key);
        const selectedMarket = metadata?.location?.market || 'All venues';
        const newSearchLoggingParams = {
            Market: selectedMarket,
            Regions: selectedRegions,
            'Multiple Regions': selectedRegions.length > 1,
            Styles: selectedStyles,
            'Multiple Styles': selectedStyles.length > 1,
            Budget: [newSearch.bmin || 0, newSearch.bmax || null],
            'Guest Count': newSearch.gcmin,
            Services: newSearch.sr,
            'Allows BYOB': newSearch.byo === '1',
            Location: viewMode === 'map' ? 'SRP map view' : 'SRP grid view',
            'SRP Version': 2,
        };
        logEvent(AnalyticsEvent.EVTNM_SUBMITTED_SEARCH_FILTERS, newSearchLoggingParams);
    }, 300);

    /*
    If the user clicks a link and an explict normalization signal is not passed,
    trigger a metadata refetch by setting the refetchSignal state value with a different one
    (it doesn't matter what as long as the shallow comparison detects a change)
    */
    React.useEffect(() => {
        history.listen((location) => {
            if (!get(location, 'state.normalization')) {
                // Cancel any pending URL changes since the link click location change should
                // take priority
                cancelOnFormChange();
                setRefetchSignal({});
            }
        });
    }, []);

    // Loading and error state rendering
    // If any fetches hard-failed (500)
    const fetchStatuses = [venuesStatus, searchFiltersStatus, metadataStatus];
    const pageData = [venues, searchFilters, metadata];

    const venuesFlattended = useMemo(() => (venues ? [...venues.venues, ...venues.backfill_venues] : []), [venues]);
    const venuesCoordinates = venues?.coordinates;

    if (fetchStatuses.some((e) => e && e.status === 'not_found')) {
        return <NotFound />;
    } else if (fetchStatuses.some((e) => e && e.status === 'permanent_redirect')) {
        return metadataStatus && metadataStatus.status === 'permanent_redirect' && metadataStatus.details ? (
            <Redirect to={metadataStatus.details} />
        ) : (
            <Error500 />
        );
    } else if (fetchStatuses.some((e) => e && e.status === 'error')) {
        return <Error500 />;
    } else if (searchFiltersIsLoading || metadataIsLoading || (!venues && venuesIsLoading)) {
        return (
            <BlurryLoader>
                <LoadingSpinner />
            </BlurryLoader>
        );
    } else if (pageData.some((e) => e === null)) {
        return <Error500 />;
    }

    // From this point down, all intiial data should be loaded

    const _metadata = metadata as Search.SearchMetadata;
    const buckets = _metadata.buckets;
    const linkFarm = _metadata.link_farm;

    const renderedVenueList = (
        <VenueListComponent
            searchResults={_venues}
            personalizedData={personalizedData}
            venuesIsLoading={venuesIsLoading}
            srpVariant={variant}
            pageMetadata={metadataParsedMemoized}
            locationsDirty={locationsTouched.current}
            metadata={_metadata}
            viewType={viewMode}
            isDW={isDw}
        />
    );

    const mapComponent = (
        <VenueMapComponent
            search={search}
            venues={venuesFlattended}
            venuesIsLoading={venuesIsLoading}
            venuesCoordinates={venuesCoordinates}
        />
    );

    const renderMap = (
        <div className={cn(_styles.mapWrapper, 'onWhenM', 'onWhenL', { [_styles.isMapMode]: viewMode === 'map' })}>{mapComponent}</div>
    );

    const filterBar = <FilterBar filtersChoices={searchFilters} selectedMarket={metadata?.location?.market || 'All venues'} />;

    const innerResultsHeaderRow = (
        <>
            {viewMode === 'grid' ? <span>{searchResultsText}</span> : filterBar}
            {/* There's some weird behavior with ButtonGroup
                component where if undefined is passed as the selected
                prop value, it wont initialize as controlled, so we pass -1
                here which seems to work */}
            {!isDw && (
                <ButtonGroup
                    selected={!viewMode ? -1 : viewMode === 'grid' ? 0 : 1}
                    onChange={(idx: number) => {
                        const view = (['grid', 'map'] as const)[idx || 0];
                        setViewMode(view);
                    }}
                    size='s'
                >
                    <ButtonGroup.Item text='Grid' icon={GridIcon} />
                    <ButtonGroup.Item text='Map' icon={MapPinIcon} />
                </ButtonGroup>
            )}
        </>
    );

    const resultsHeaderRow = (
        <div className={cn({ [_styles.isMap]: viewMode === 'map' }, _styles.resultsHeaderRow)}>{innerResultsHeaderRow}</div>
    );

    const marketLinks = _metadata?.market_links;

    const renderHeaderHero = () => {
        const isHero = reactRouterLocation.pathname.match('^/(rehearsal-.*|affordable-.*|[a-zA-Z0-9-]*-wedding-venues)/');
        const searchBar = marketLinks ? <MarketLinksContainer marketLinks={marketLinks} /> : <Typeahead isSearchIconCarinaShown={true} />;

        const className = isHero ? cn(_styles.webHeaderHero, 'onWhenM', 'onWhenL') : cn(_styles.noHeroHeader, 'onWhenM', 'onWhenL');

        return (
            <div className={className}>
                {renderedBreadcrumbs}
                {isHero && searchBar}
            </div>
        );
    };

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name='description' content={metaDescription} />
                {keywords && <meta name='keywords' content={keywords} />}
                {metaLinks && <link rel='canonical' href={HOGAN_CONFIG.ws_api_client.base_url + metaLinks.canonical} />}
                {metaLinks && metaLinks.next && <link rel='next' href={metaLinks.next} />}
                {metaLinks && metaLinks.prev && <link rel='prev' href={metaLinks.prev} />}
            </Helmet>
            <LiveAgent />

            {!isViewChosen && isFromMarketListForMapViewAB(match.params?.market) && (
                <BlurryLoader>
                    <LoadingSpinner />
                </BlurryLoader>
            )}
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validate={onFormChange}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(false); // We don't need submit, but typing said that onSubmit is required props
                }}
            >
                {(formikProps) => {
                    LogPerformance('formik SRP');

                    // Update the formik values state that's used to update page metadata
                    const regionsTouched = formikProps.touched.region;
                    if (regionsTouched && !locationsTouched.current) {
                        locationsTouched.current = true;
                    }

                    return (
                        <Breakpoints>
                            <StickyContainer>
                                <div className='srp-wrapper'>
                                    <OnWhen m={true} l={true}>
                                        {renderHeaderHero()}
                                    </OnWhen>
                                    {viewMode === 'grid' && (
                                        <OnWhen s={true}>
                                            <header className={cn(_styles.mwebHeader, 'onWhenS')}>
                                                {/* Sticky bar with filter buttons */}
                                                {filterBar}
                                            </header>
                                        </OnWhen>
                                    )}

                                    <div className='src-container'>
                                        <OnWhen s={true}>
                                            <div className={cn(_styles.mwebBreadcrumbsOverlay, 'onWhenS')} ref={breadcrumbsRef}>
                                                {renderedBreadcrumbs}
                                            </div>
                                        </OnWhen>

                                        <h1>{titleText}</h1>

                                        {resultsHeaderRow}

                                        <div
                                            className={cn(
                                                { [_styles.mapVenueListOverflowWrapper]: viewMode === 'map' },
                                                'onWhenM',
                                                'onWhenL'
                                            )}
                                        >
                                            <OnWhen m={true} l={true}>
                                                <Row id='stickyBoundary'>
                                                    {viewMode === 'grid' && (
                                                        <Col
                                                            s={{ width: 'fill' }}
                                                            m={{ width: 280 }}
                                                            l={{ width: 280 }}
                                                            padding={{ top: 48, end: 0, bottom: 0, start: 0 }}
                                                            offsetEnd={24}
                                                        >
                                                            <Sticky
                                                                bottomBoundary={'#stickyBoundary'}
                                                                activeClass='filters-sticky-with-sticky-header'
                                                                releasedClass='filters-sticky-with-sticky-released'
                                                            >
                                                                {searchFilters ? (
                                                                    <FilterSidebar
                                                                        searchFilters={searchFilters}
                                                                        selectedMarket={metadata?.location?.market || 'All venues'}
                                                                    />
                                                                ) : (
                                                                    <div>Loading...</div>
                                                                )}
                                                            </Sticky>
                                                        </Col>
                                                    )}
                                                    <Col
                                                        s={{ width: 'fill' }}
                                                        m={{ width: 'fill' }}
                                                        l={{ width: 'fill' }}
                                                        padding={0}
                                                        offsetEnd={viewMode === 'grid' ? 0 : 35}
                                                    >
                                                        {(viewMode === 'grid' || gridTouched) && (
                                                            <div className={cn('is-grid', { ['container-hidden']: viewMode !== 'grid' })}>
                                                                {renderedVenueList}
                                                                {buckets && buckets.length > 0 && (
                                                                    <CuratedLists
                                                                        data={buckets}
                                                                        srpLocation={_metadata.location ? _metadata.location : undefined}
                                                                        style={_metadata.style_name}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                        {(viewMode === 'map' || mapTouched) && (
                                                            <div className={cn({ ['container-hidden']: viewMode !== 'map' })}>
                                                                {renderMap}
                                                            </div>
                                                        )}
                                                    </Col>
                                                    {viewMode === 'map' && (
                                                        <Col
                                                            padding={{ top: 0, end: 0, bottom: 40, start: 0 }}
                                                            s={{ width: 770 }}
                                                            m={{ width: 770 }}
                                                            l={{ width: 770 }}
                                                        >
                                                            <div className={cn('is-map', _styles.mapVenueListOverflow)}>
                                                                {renderedVenueList}
                                                            </div>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </OnWhen>
                                        </div>

                                        <div className={'onWhenS'}>
                                            <OnWhen s={true}>
                                                <Row>
                                                    <Col s={{ width: 'fill' }} m={{ width: 'fill' }} l={{ width: 'fill' }} padding={0}>
                                                        {renderedVenueList}
                                                    </Col>
                                                </Row>
                                            </OnWhen>
                                        </div>
                                        <div className={_styles.footer}>
                                            {linkFarm && <LinkFarm data={linkFarm.filter((l) => !!l) as Search.LinkFarmSection[]} />}
                                            {_metadata.seo_blurb && <SeoBlurb data={_metadata.seo_blurb} expandText='Show more...' />}
                                        </div>
                                    </div>
                                </div>
                            </StickyContainer>
                        </Breakpoints>
                    );
                }}
            </Formik>
        </>
    );
};

(SearchResultPageComponent as ComponentWithSSRHooks).apiHooks = [useVenues, usePageMetadata, useFilters];

// react-router route component "params" props
export type SRPRouteMatchProps = {
    market?: string;
    submarket?: string;
    region?: string;
    zipcode?: string;
    style?: string;
};
